import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { IPublicFilterJob, IPublicApiJobFilters } from 'core/interface/job';
import useLocales from '../../../hooks/useLocales';
import { JobService } from 'services/jobs/job';
import { getCareerLevelLabels, getSalaryLabels } from 'constants/constants';
import Checkboxes from 'components/shared/Checkbox/Index';
import Autocomplete from 'components/shared/Autocomplete/Index';
import Button from 'components/Button/Index';
import { ProfileService } from 'services/profile/profile';
import DropDown from 'components/Dropdown';
import {
  degreeTypes,
  companySizeChoices
} from 'core/constants/aboutMePopUpConstants';
import { ILocationRedux, IndustryRedux } from 'core/interface/education';
import { SkillService } from 'services/skills/skillService';
import { SearchInitialValue } from 'core/interface/searchInitialValue';
import { Industries } from 'core/interface/education-side';
import { IAutocompleteItem } from 'core/interface/autocompleteItem';
import { Form, Formik } from 'formik';
import { contractTypes } from 'core/constants/workType';
import { hourlyRateChoices } from 'core/constants/preferenceConstats';

import styles from './index.module.scss';
import { ICandidateLanguage } from 'core/interface/candidate-language';
import { useDebounce } from 'helpers/useDebounce';

const searchInitialValue: SearchInitialValue<
  Industries[] | IAutocompleteItem[] | any | null
> = {
  industry: {
    value: '',
    interval: null,
    func: (query: string) => ProfileService.getIndustries(query)
  },
  computer_skill: {
    value: '',
    interval: null,
    func: (query: string) => SkillService.getITSkillsList(query)
  },
  location: {
    value: '',
    interval: null,
    func: (query: string) =>
      JobService.getLocations({
        q: query
      })
  }
};

interface FilterState {
  q: string;
  industries: string;
  locations: {
    id: string | number;
    name: string;
  };
  skills: string[];
  degrees: string;
  salaries: string[];
  work_exps: string[];
  languages: ICandidateLanguage[];
  is_remote: boolean;
  contract_types: string[];
  company_sizes: string[];
  [key: string]:
    | string
    | string[]
    | boolean
    | ICandidateLanguage[]
    | { id: string | number; name: string };
}

const PublicJobFilters: FC<IPublicFilterJob> = ({
  applyFilter
}): JSX.Element => {
  const { t } = useLocales();
  const industries = useSelector(
    (selector: IndustryRedux) => selector.industries?.industry || []
  );

  const cities = useSelector(
    (selector: ILocationRedux) => selector.locations?.cities || []
  );

  const [resetDropdown, setResetDropdown] = useState<boolean>(false);
  const [values, setValues] = useState<FilterState>({
    q: '',
    industries: '',
    locations: {
      id: '',
      name: ''
    },
    skills: [],
    degrees: '',
    salaries: [],
    work_exps: [],
    languages: [],
    is_remote: false,
    contract_types: [],
    company_sizes: []
  });

  // Add debounced search value
  const debouncedSearchQuery = useDebounce(values.q, 500);

  const [search, setSearch] =
    useState<
      SearchInitialValue<Industries[] | IAutocompleteItem[] | any | null>
    >(searchInitialValue);

  const handleCheckbox = (e: string, key: string): void => {
    const value = values[key] as string[];
    const index = value.indexOf(e);
    if (index !== -1) {
      value.splice(index, 1);
    } else {
      value.push(e);
    }

    setValues({
      ...values,
      [key]: value
    });
  };

  const onInputChange = (e: string, type: string): void => {
    const searchNewData = { ...searchInitialValue };
    searchNewData[type].value = e;
    if (searchNewData[type].interval) {
      clearTimeout(searchNewData[type].interval as NodeJS.Timeout);
    }
    searchNewData[type].interval = setTimeout(() => {
      searchNewData[type].func(searchNewData[type].value);
    }, 500);
    setSearch(searchNewData);
  };

  useEffect(() => {
    Object.values(search).map((e) => {
      e.func(e.value);
    });
  }, []);

  // Effect to trigger search when debounced search query changes
  useEffect(() => {
    if (debouncedSearchQuery !== undefined) {
      // Create API params with current filter values
      const apiParams: IPublicApiJobFilters = {
        q: debouncedSearchQuery,
        industries: values.industries,
        locations: values.locations.id ? String(values.locations.id) : '',
        skills: values.skills.join(','),
        salaries: values.salaries.join(','),
        work_exps: values.work_exps.join(','),
        company_sizes: values.company_sizes.join(','),
        contract_types: values.contract_types.join(','),
        languages: '',
        is_remote: values.is_remote,
        degrees: values.degrees
      };

      // Apply filter with debounced search query
      applyFilter(apiParams);
    }
  }, [debouncedSearchQuery]);

  const handleFilter = (): void => {
    // Convert array values to comma-separated strings as required by the API
    const apiParams: IPublicApiJobFilters = {
      q: values.q,
      industries: values.industries,
      locations: values.locations.id ? String(values.locations.id) : '',
      skills: values.skills.join(','),
      salaries: values.salaries.join(','),
      work_exps: values.work_exps.join(','),
      company_sizes: values.company_sizes.join(','),
      contract_types: values.contract_types.join(','),
      languages: '', // We're not using languages in this implementation
      is_remote: values.is_remote,
      degrees: values.degrees
    };

    applyFilter(apiParams);
  };

  const handleLoadMoreCities = () => {
    const params = { limit: 10, offset: cities.length };
    JobService.getLocations(params, true);
  };

  const handleResetFilters = () => {
    const val: FilterState = {
      q: '',
      industries: '',
      locations: {
        id: '',
        name: ''
      },
      skills: [],
      degrees: '',
      salaries: [],
      work_exps: [],
      languages: [],
      is_remote: false,
      contract_types: [],
      company_sizes: []
    };

    setValues(val);

    // Create a properly formatted API params object
    const apiParams: IPublicApiJobFilters = {
      q: '',
      industries: '',
      locations: '',
      skills: '',
      degrees: '',
      salaries: '',
      work_exps: '',
      is_remote: false,
      contract_types: '',
      company_sizes: '',
      languages: '' // We're not using languages in this implementation
    };

    applyFilter(apiParams);
    setResetDropdown(true);
    setSearch({
      industry: {
        value: '',
        interval: null,
        func: (query: string) => ProfileService.getIndustries(query)
      },
      computer_skill: {
        value: '',
        interval: null,
        func: (query: string) => SkillService.getITSkillsList(query)
      },
      location: {
        value: '',
        interval: null,
        func: (query: string) =>
          JobService.getLocations({
            q: query
          })
      }
    });

    // Reset all dropdowns and multiselects by forcing a re-render
    setResetDropdown(false);
    setTimeout(() => {
      setResetDropdown(true);
    }, 0);
  };

  const handleClearIndustry = () => {
    const updatedValues = {
      ...values,
      industries: ''
    };
    setValues(updatedValues);

    // Create a properly formatted API params object
    const apiParams: IPublicApiJobFilters = {
      ...updatedValues,
      locations: updatedValues.locations.id
        ? String(updatedValues.locations.id)
        : '',
      skills: updatedValues.skills.join(','),
      salaries: updatedValues.salaries.join(','),
      work_exps: updatedValues.work_exps.join(','),
      company_sizes: updatedValues.company_sizes.join(','),
      contract_types: updatedValues.contract_types.join(','),
      languages: '' // We're not using languages in this implementation
    };

    applyFilter(apiParams);
  };

  const handleClearDegreeType = () => {
    const updatedValues = {
      ...values,
      degrees: ''
    };
    setValues(updatedValues);

    // Create a properly formatted API params object
    const apiParams: IPublicApiJobFilters = {
      ...updatedValues,
      locations: updatedValues.locations.id
        ? String(updatedValues.locations.id)
        : '',
      skills: updatedValues.skills.join(','),
      salaries: updatedValues.salaries.join(','),
      work_exps: updatedValues.work_exps.join(','),
      company_sizes: updatedValues.company_sizes.join(','),
      contract_types: updatedValues.contract_types.join(','),
      languages: '' // We're not using languages in this implementation
    };

    applyFilter(apiParams);
  };

  const handleClearLocation = () => {
    const updatedValues = {
      ...values,
      locations: {
        id: '',
        name: ''
      }
    };
    setValues(updatedValues);

    // Create a properly formatted API params object
    const apiParams: IPublicApiJobFilters = {
      q: updatedValues.q,
      industries: updatedValues.industries,
      locations: '', // Clear the locations
      skills: updatedValues.skills.join(','),
      salaries: updatedValues.salaries.join(','),
      work_exps: updatedValues.work_exps.join(','),
      company_sizes: updatedValues.company_sizes.join(','),
      contract_types: updatedValues.contract_types.join(','),
      languages: '', // We're not using languages in this implementation
      is_remote: updatedValues.is_remote,
      degrees: updatedValues.degrees
    };

    applyFilter(apiParams);
  };

  // Helper function to get compensations labels based on contract type
  const getSalaryLabelsByContractType = (
    salaries: string[],
    contractTypes: string[]
  ) => {
    // If contract includes "2" (Freelance), show hourly rate choices
    if (contractTypes.includes('2')) {
      return hourlyRateChoices.map((choice) => [
        {
          label: String(choice.to_show),
          isChecked: salaries?.includes(String(choice.value)),
          value: String(choice.value)
        }
      ]);
    }
    // Otherwise show regular compensations labels
    return getSalaryLabels(salaries);
  };

  // Function to render compensation section with appropriate headers
  const renderCompensationSection = () => {
    const salaryLabels = getSalaryLabelsByContractType(
      values.salaries,
      values.contract_types
    );
    // Only show both types if freelance is selected along with other contract types
    const showBothTypes =
      values.contract_types.includes('2') && values.contract_types.length > 1;
    // Show only hourly rates if only freelance is selected
    const showOnlyHourlyRates =
      values.contract_types.includes('2') && values.contract_types.length === 1;
    // Show monthly salary by default or if non-freelance contract types are selected
    const showMonthlySalary = !showOnlyHourlyRates;

    return (
      <div className={styles.filters_checkbox}>
        <h1>{t('Compensations (EUR)')}</h1>

        {showBothTypes && (
          <h3 className={styles.section_header}>{t('Monthly Salary')}</h3>
        )}

        {/* Show monthly salary options by default or if non-freelance contract types are selected */}
        {showMonthlySalary &&
          getSalaryLabels(values.salaries).map((e, i) => (
            <Checkboxes
              checkboxes={e}
              onArrayChange={(e) => {
                if (Array.isArray(e)) {
                  handleCheckbox(e[0].value ?? e[0].label, 'salaries');
                }
              }}
              key={`monthly-${i}`}
            />
          ))}

        {/* Show hourly rate options with header if needed */}
        {showBothTypes && (
          <>
            <h3 className={styles.section_header}>{t('Hourly Rate')}</h3>
            {hourlyRateChoices.map((choice, i) => (
              <Checkboxes
                checkboxes={[
                  {
                    label: String(choice.to_show),
                    isChecked: values.salaries?.includes(String(choice.value)),
                    value: String(choice.value)
                  }
                ]}
                onArrayChange={(e) => {
                  if (Array.isArray(e)) {
                    handleCheckbox(e[0].value ?? e[0].label, 'salaries');
                  }
                }}
                key={`hourly-${i}`}
              />
            ))}
          </>
        )}

        {/* Show only hourly rates if only freelance is selected */}
        {showOnlyHourlyRates &&
          salaryLabels.map((e, i) => (
            <Checkboxes
              checkboxes={e}
              onArrayChange={(e) => {
                if (Array.isArray(e)) {
                  handleCheckbox(e[0].value ?? e[0].label, 'salaries');
                }
              }}
              key={i}
            />
          ))}
      </div>
    );
  };

  return (
    <Formik
      className={styles.filters}
      initialValues={{}}
      onSubmit={(e) => console.log(e)}
    >
      <Form className={styles.filters}>
        <div className={styles.filters}>
          <h1>{t('Filter')}</h1>

          {/* Search Input */}
          <div className={styles.filters_search_input}>
            <h1>{t('Search Jobs')}</h1>
            <div className={styles.input_container}>
              <input
                type="text"
                className={styles.input}
                placeholder={t('Search by job title, keyword, etc.')}
                value={values.q}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setValues({
                    ...values,
                    q: e.target.value
                  });
                }}
              />
            </div>
          </div>

          {/* 1. Industry */}
          <div className={styles.filters_checkbox}>
            <h1 className="label">{t('Industry')}</h1>
            <Autocomplete
              fieldName="industry"
              show_arrow={true}
              value={
                industries.find(
                  (industry) =>
                    String(industry?.id) === String(values.industries)
                )?.name ?? ''
              }
              options={
                industries?.map((e) => ({
                  id: e?.id,
                  title: e?.name
                })) || []
              }
              show_location={false}
              placeholder={t('Industry')}
              onChange={(e) => {
                const activeItem = String(
                  industries.find((industry) => industry.name === e)?.id
                );
                setValues((prev) => ({
                  ...prev,
                  industries: activeItem
                }));
                onInputChange(e, 'industry');
              }}
              onInputChange={(e) => onInputChange(e, 'industry')}
              values={industries}
              onClear={handleClearIndustry}
            />
          </div>

          {/* 2. Location */}
          <div className={styles.filters_checkbox}>
            <h1 className="label">{t('Location')}</h1>
            <Autocomplete
              fieldName="location"
              show_arrow={true}
              value={values.locations.name}
              options={
                cities?.map((e) => ({
                  id: e?.id,
                  title: e?.display_name
                })) || []
              }
              show_location={true}
              placeholder={t('Location')}
              onChange={(location) => {
                const activeItem = cities.find(
                  (city) => city.display_name === location
                );

                setValues({
                  ...values,
                  locations: {
                    id: activeItem?.id || '',
                    name: activeItem?.display_name || ''
                  }
                });
                onInputChange(location, 'location');
              }}
              onInputChange={(e) => onInputChange(e, 'location')}
              values={cities}
              loadMore={handleLoadMoreCities}
              onClear={handleClearLocation}
            />
          </div>

          {/* 3. Degree Type */}
          <div className={styles.filters_checkbox}>
            <h1 className="label">{t('Degree Type')}</h1>
            <DropDown
              onReset={resetDropdown}
              defaultValue={values.degrees}
              placeholder={t('Degree type')}
              dropdownClassName={`dashboard__left-dropdown ${styles.filters_dropdown}`}
              items={degreeTypes}
              selectedValue={(e) =>
                setValues((prev) => ({ ...prev, degrees: e }))
              }
              onClear={handleClearDegreeType}
            />
          </div>
          <div className={styles.filters_wrapper}>
            {/* 4. Contract Type */}
            <div className={styles.filters_checkbox}>
              <h1>{t('Contract Type')}</h1>
              {contractTypes.map((contract, i) => (
                <Checkboxes
                  key={i}
                  checkboxes={[
                    {
                      label: String(contract.to_show),
                      value: String(contract.value),
                      isChecked: values.contract_types.includes(
                        String(contract.value)
                      )
                    }
                  ]}
                  onArrayChange={(e) => {
                    if (
                      Array.isArray(e) &&
                      e[0] &&
                      (e[0].value || e[0].label)
                    ) {
                      const value = e[0].value
                        ? String(e[0].value)
                        : e[0].label;
                      handleCheckbox(value, 'contract_types');
                    }
                  }}
                />
              ))}
            </div>

            {/* 5. Compensations */}
            {renderCompensationSection()}

            {/* 6. Career level */}
            <div className={styles.filters_checkbox}>
              <h1>{t('Career level')}</h1>
              {getCareerLevelLabels(values.work_exps).map((e, i) => (
                <Checkboxes
                  checkboxes={e}
                  onArrayChange={(e) => {
                    if (Array.isArray(e)) {
                      handleCheckbox(e[0].value ?? e[0].label, 'work_exps');
                    }
                  }}
                  key={i}
                />
              ))}
            </div>

            {/* 7. Company Size */}
            <div className={styles.filters_checkbox}>
              <h1>{t('Company Size')}</h1>
              {companySizeChoices.map((companySize, i) => (
                <Checkboxes
                  key={i}
                  checkboxes={[
                    {
                      label: String(companySize.to_show),
                      value: String(companySize.value),
                      isChecked:
                        Array.isArray(values.company_sizes) &&
                        values.company_sizes.includes(String(companySize.value))
                    }
                  ]}
                  onArrayChange={(e) => {
                    if (
                      Array.isArray(e) &&
                      e[0] &&
                      (e[0].value || e[0].label)
                    ) {
                      const value = e[0].value
                        ? String(e[0].value)
                        : e[0].label;
                      handleCheckbox(value, 'company_sizes');
                    }
                  }}
                />
              ))}
            </div>
          </div>

          {/* 8. Is Remote */}
          <div className={styles.filters_checkbox}>
            <h1>{t('Is a remote job')}</h1>
            <Checkboxes
              onArrayChange={(e) => {
                if (Array.isArray(e)) {
                  setValues((prev) => ({
                    ...prev,
                    is_remote: e[0].isChecked || false
                  }));
                }
              }}
              checkboxes={[
                {
                  label: t('Remote'),
                  isChecked: values?.is_remote
                }
              ]}
            />
          </div>

          <div>
            <Button
              t={t}
              className={`navbar__left-btn ${styles.filters_submit} ${styles.filters_buttons}`}
              title={t('Filter')}
              onClick={handleFilter}
            />
          </div>
          <div className={styles.filters_reset_button}>
            <Button
              t={t}
              className={`navbar__left-btn ${styles.filters_reset} ${styles.filters_buttons}`}
              title={t('Reset Filters')}
              onClick={handleResetFilters}
            />
          </div>
        </div>
      </Form>
    </Formik>
  );
};

export default PublicJobFilters;
